import React from "react";
import Swal from "sweetalert2";
import Select from "react-select";
import "./ProductDetails.css";
import { getFromLocalStorage } from "../../../../utils/common-utils";
import { submitProductDetails } from "../../../../api/Api";
import Loader from "../../../../components/loader/loader";

// Custom Option component with checkbox
const Option = props => {
  return (
    <div
      className={`select-option ${props.isSelected ? 'selected' : ''}`}
      onClick={() => props.selectOption(props.data)}
    >
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="option-checkbox"
      />
      <label className="option-label">{props.label}</label>
    </div>
  );
};

const ProductDetailsTabs = ({
  setAddProductModal,
  setShowAllTabs,
  setActiveTab,
  serviceId,
  onProductCreated,
}) => {
  const [formData, setFormData] = React.useState({
    productName: "",
    language: "English",
    category: "",
    packageValidityFrom: "",
    packageValidityTo: "",
    productId: "",
    alphaNumeric: "",
    // operatorServiceId: "",
    subscriptionChannel: [],
    unsubscriptionChannel: [],
    productDescription: "",
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 23:59:59`;
  };

  const handleChange = (name, value) => {
    // Clear error when user makes a change
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    if (Array.isArray(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value.map(option => option.value)
      }));
    } else if (name === 'packageValidityFrom') {
      setFormData(prev => ({
        ...prev,
        packageValidityFrom: value,
        packageValidityTo: prev.packageValidityTo && prev.packageValidityTo < value ? '' : prev.packageValidityTo
      }));
    } else if (name === 'packageValidityTo') {
      if (value >= formData.packageValidityFrom) {
        setFormData(prev => ({
          ...prev,
          packageValidityTo: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.productName?.trim()) {
      newErrors.productName = 'Product Name is required';
    }

    if (!formData.language) {
      newErrors.language = 'Language is required';
    }

    if (!formData.packageValidityFrom) {
      newErrors.packageValidityFrom = 'Package Validity From is required';
    }

    if (!formData.packageValidityTo) {
      newErrors.packageValidityTo = 'Package Validity To is required';
    }

    if (!formData.category?.trim()) {
      newErrors.category = 'Category is required';
    }

    // Subscription Channel validation
    if (!formData.subscriptionChannel?.length) {
      newErrors.subscriptionChannel = 'Please select at least one Subscription Channel';
    }

    // Unsubscription Channel validation
    if (!formData.unsubscriptionChannel?.length) {
      newErrors.unsubscriptionChannel = 'Please select at least one Unsubscription Channel';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProductDetailsSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in all required fields",
      });
      return;
    }

    try {
      setIsLoading(true);
      // Convert arrays to comma-separated strings and ensure productId is number
      const payload = {
        productName: formData.productName,
        language: formData.language,
        packageValidityForm: formatDate(formData.packageValidityFrom),
        packageValidityTo: formatDate(formData.packageValidityTo),
        category: formData.category,
        channel: Array.isArray(formData.subscriptionChannel) 
          ? formData.subscriptionChannel.join(',') 
          : formData.subscriptionChannel,
        productId: Number(formData.productId) || null, // Convert to number
        operatorServiceId: formData.operatorServiceId || null,
        unsubChannel: Array.isArray(formData.unsubscriptionChannel) 
          ? formData.unsubscriptionChannel.join(',') 
          : formData.unsubscriptionChannel,
        description: formData.productDescription || null,
        alphaNumeric: formData.alphaNumeric || null,  
        serviceId: serviceId,
        userId: getFromLocalStorage("userData")?.id,
      };

      const response = await submitProductDetails(payload);
    console.log("API Response:", response); // Add this for debugging

    // Check if response exists and has the required data
    if (response && response.statusDescription && response.statusDescription.status === 200) {
      // Make sure productModel exists before accessing its id
      if (response.productModel && response.productModel.id) {
        onProductCreated(response.productModel.id);
        // setShowAllTabs(true);
        setActiveTab("billing");
        
        Swal.fire({
          icon: "success",
          text: response.statusDescription.description || "Product details saved successfully!",
          confirmButtonText: "OK"
        });
      } else {
        throw new Error("Product ID not found in response");
      }
    } else {
      // Handle case where response is not successful
      throw new Error(response?.statusDescription?.description || "Failed to save product details");
    }
  } catch (error) {
    console.error("Error submitting product details:", error);
    
    Swal.fire({
      icon: "error",
      text: error.message || "Failed to save product details. Please try again.",
      confirmButtonText: "OK"
    });
  } finally {
    setIsLoading(false);
  }
}

  const channelOptions = [
    { value: 'CCI', label: 'CCI' },
    { value: 'SMS', label: 'SMS' },
    { value: 'WEB', label: 'WEB' },
    { value: 'APP', label: 'APP' }
  ];

  return (
    <>
    {isLoading && <Loader />}
    <div className="tab-pane active">
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Product Name<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              className="form-control"
              name="productName"
              value={formData.productName}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Product Name"
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Language<span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="language"
              value={formData.language}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            >
              <option value="English">English</option>
              <option value="Arabic">Arabic</option>
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Package validity From<span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="packageValidityFrom"
              value={formData.packageValidityFrom}
              onChange={(e) => {
                const selectedDate = e.target.value;
                // Reset validity to date if the new from date is after the current to date
                if (formData.packageValidityTo && selectedDate > formData.packageValidityTo) {
                  handleChange('packageValidityTo', '');
                }
                handleChange(e.target.name, selectedDate);
              }}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Package validity To<span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="packageValidityTo"
              value={formData.packageValidityTo}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate < formData.packageValidityFrom) {
                  Swal.fire({
                    icon: "error",
                    text: "End date must be after start date"
                  });
                  return;
                }
                handleChange(e.target.name, selectedDate);
              }}
              min={formData.packageValidityFrom}
              disabled={!formData.packageValidityFrom}
            />
            {formData.packageValidityFrom && !formData.packageValidityTo && (
              <small className="text-muted">
                Please select an end date after {new Date(formData.packageValidityFrom).toLocaleDateString()}
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Category<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              className="form-control"
              name="category"
              value={formData.category}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Category"
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Product-Id</label>
            <input
              type="text"
              className="form-control"
              maxLength={50}
              name="productId"
              value={formData.productId}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Product ID"
            />
          </div>
        </div>
        <div className="col-md-4 sub_chan">
          <div className="form-group">
            <label>
              Subscription Channel<span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="subscriptionChannel"
              options={channelOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              components={{ Option }}
              value={channelOptions.filter(option => 
                formData.subscriptionChannel?.includes(option.value)
              )}
              onChange={(selectedOptions) => 
                handleChange('subscriptionChannel', selectedOptions)
              }
              placeholder="Select Channel(s)"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
            />
          </div>
        </div>
        <div className="col-md-4 sub_chan">
          <div className="form-group">
            <label>
              Unsubscription Channel<span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="unsubscriptionChannel"
              options={channelOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              components={{ Option }}
              value={channelOptions.filter(option => 
                formData.unsubscriptionChannel?.includes(option.value)
              )}
              onChange={(selectedOptions) => 
                handleChange('unsubscriptionChannel', selectedOptions)
              }
              placeholder="Select Channel(s)"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Operator Service ID</label>
            <input
              type="text" 
              maxLength={50}
              className="form-control"  
              name="operatorServiceId"
              value={formData.operatorServiceId}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Operator Service ID"
            />
          </div>  
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Alpha Numeric</label>
            <input
              type="text"
              className="form-control"
              name="alphaNumeric"
              maxLength={50}
              value={formData.alphaNumeric}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Alpha Numeric"
            />
          </div>
        </div>

        <div className="col-md-8">
          <div className="form-group">
            <label>Product Description</label>
            <textarea
              className="form-control"
              name="productDescription"
              value={formData.productDescription}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              rows="3"
              placeholder="Enter Product Description"
            ></textarea>
          </div>
        </div>
      </div>

      {/* <div className="row mt-3">
       
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Package validity To<span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="packageValidityTo"
              value={formData.packageValidityTo}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              min={formData.packageValidityFrom || new Date().toISOString().split('T')[0]}
              disabled={!formData.packageValidityFrom}
            />
            {formData.packageValidityFrom && !formData.packageValidityTo && (
              <small className="text-muted">
                Please select an end date after {new Date(formData.packageValidityFrom).toLocaleDateString()}
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Category<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="category"
              value={formData.category}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Category"
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Product-Id</label>
            <input
              type="text"
              className="form-control"
              name="productId"
              value={formData.productId}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Product ID"
            />
          </div>
        </div>
      </div> */}

     

      {/* <div className="row mt-3">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Subscription Channel<span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="subscriptionChannel"
              options={channelOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              components={{ Option }}
              value={channelOptions.filter(option => 
                formData.subscriptionChannel?.includes(option.value)
              )}
              onChange={(selectedOptions) => 
                handleChange('subscriptionChannel', selectedOptions)
              }
              placeholder="Select Channel(s)"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              Unsubscription Channel<span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="unsubscriptionChannel"
              options={channelOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              components={{ Option }}
              value={channelOptions.filter(option => 
                formData.unsubscriptionChannel?.includes(option.value)
              )}
              onChange={(selectedOptions) => 
                handleChange('unsubscriptionChannel', selectedOptions)
              }
              placeholder="Select Channel(s)"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Operator Service ID</label>
            <input
              type="text" 
              className="form-control"  
              name="operatorServiceId"
              value={formData.operatorServiceId}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter Operator Service ID"
            />
          </div>  
        </div>
      </div> */}

      {/* <div className="row mt-3">
        <div className="col-md-12">
          <div className="form-group">
            <label>Product Description</label>
            <textarea
              className="form-control"
              name="productDescription"
              value={formData.productDescription}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              rows="3"
              placeholder="Enter Product Description"
            ></textarea>
          </div>
        </div>
      </div> */}

      <div className="row mt-4">
        <div className="col-md-12 d-flex justify-content-between buttons_divs">
          <button
            type="button"
            className="btn btn-danger mr-2"
            onClick={() => setAddProductModal(false)}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
              onClick={handleProductDetailsSubmit}
            disabled={isLoading}
          >
          Save & Continue
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProductDetailsTabs;
